import { Link } from "react-router-dom";
import { formatBlogDateShort } from "@/blog/format";
import { BLOG_ROUTE_BY_ID, blogPostPath } from "@/blog/routes";
import type { BlogPostSummary } from "@/blog/types";
import { BlogCover } from "./BlogCover";

export function RouteChip({ post, className = "" }: { post: BlogPostSummary; className?: string }) {
  const route = BLOG_ROUTE_BY_ID[post.route];
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full bg-white/95 px-3 py-1 text-xs font-semibold text-teal-strong shadow-[0_4px_14px_-6px_rgb(26_60_94/0.35)] ${className}`}
    >
      <span aria-hidden="true" className="size-1.5 rounded-full bg-brand-secondary" />
      {route.label}
      {post.routeStep ? ` · Paso ${post.routeStep}` : ""}
    </span>
  );
}

/** Article card: the whole card is one link; the title carries the name. */
export function BlogCard({
  post,
  headingLevel: Heading = "h3",
  feature = false,
  priority = false,
}: {
  post: BlogPostSummary;
  headingLevel?: "h2" | "h3";
  feature?: boolean;
  priority?: boolean;
}) {
  return (
    <article
      className={`group/card relative isolate flex h-full overflow-hidden rounded-[1.75rem] border border-brand-primary/8 bg-white shadow-lit transition-[box-shadow,translate] duration-500 ease-[var(--ease-out-expo)] hover:-translate-y-1 hover:shadow-lit-lg ${
        feature ? "flex-col lg:grid lg:grid-cols-[1.15fr_1fr]" : "flex-col"
      }`}
    >
      <div className={`relative overflow-hidden ${feature ? "aspect-[16/10] lg:aspect-auto lg:min-h-[22rem]" : "aspect-[16/10]"}`}>
        <BlogCover
          post={post}
          priority={priority}
          sizes={feature ? "(min-width: 1024px) 55vw, 100vw" : undefined}
          className="transition-transform duration-700 ease-[var(--ease-out-expo)] group-hover/card:scale-[1.03]"
        />
        <RouteChip post={post} className="absolute bottom-4 left-4" />
      </div>
      <div className={`flex flex-1 flex-col ${feature ? "gap-4 p-7 sm:p-9 lg:justify-center lg:p-11" : "gap-3 p-6 sm:p-7"}`}>
        <Heading
          className={`font-display font-semibold leading-[1.15] tracking-[-0.015em] text-brand-primary ${
            feature ? "text-2xl sm:text-3xl" : "text-2xl"
          }`}
        >
          <Link
            to={blogPostPath(post.slug)}
            className="rounded-md transition-colors duration-300 after:absolute after:inset-0 after:content-[''] group-hover/card:text-teal-strong"
          >
            {post.title}
          </Link>
        </Heading>
        <p className={`leading-relaxed text-ink-soft ${feature ? "text-lg" : "text-base"}`}>{post.excerpt}</p>
        <p className="mt-auto pt-2 text-sm text-ink-muted">
          <time dateTime={post.publishedAt}>{formatBlogDateShort(post.publishedAt)}</time>
          <span aria-hidden="true"> · </span>
          {post.readingMinutes} min de lectura
        </p>
      </div>
    </article>
  );
}
